import { Button, Spinner, Text } from "@ttc3k/trekker";
import { useTagEntityUsage, UseTagEntityUsageProps } from "./useTagEntityUsage";
import { useTranslation } from "react-i18next";
import { Center, Flex } from "styled-system/jsx";
import { SimpleErrorNotification, ViewSubSection, OperatorProfileFlyout } from "shared/components";
import { EnumOwnerType, Multilingual } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { ArrowRightCircle } from "iconoir-react";
import { EventCreateEditModal } from "shared/components/organisms/EventCreationAndEditing/EventCreateEditModal";
import { useLoggedInAs } from "shared/hooks";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";
import { useMemo, useState } from "react";

/**
 * A single entity usage item
 * - Displays the name of the entity
 * - Displays the name of the parent entity
 * - Navigates to the entity (or as close to it as possible) when clicked
 * @param param0
 * @returns
 */
const TagEntity = ({
  entityType,
  entityId,
  entityName,
  parentId,
  parentName,
  onOpenEvent,
  onOpenOperator,
}: {
  entityType:
    | "operators"
    | "events"
    | "products"
    | "lists"
    | "widgets"
    | "people";
  entityId: string;
  entityName: Multilingual;
  parentId: string;
  parentName: Multilingual;
  onOpenEvent?: () => void;
  onOpenOperator?: () => void;
}) => {
  const loggedInAs = useLoggedInAs();
  if (!entityId) return null;

  const organizationId =
    loggedInAs && loggedInAs.type === EnumOwnerType.Organization
      ? loggedInAs.entityId
      : parentId;

  const handleNavigateToEntity = () => {
    switch (entityType) {
      case "operators": {
        if (onOpenOperator) {
          onOpenOperator();
        } else {
          window.open(
            `/app/organization/${organizationId}/directory/${entityId}`,
            "_blank",
            "noopener,noreferrer",
          );
        }
        break;
      }
      case "events":
        onOpenEvent?.();
        break;
      case "products":
        /*
         * Products belong to a storefront, but the product may not be
         * used in a booker, so just opening the storefront for now...
         */
        window.open(
          `/app/storefront/${parentId}`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case "lists":
        window.open(
          `/app/organization/${organizationId}/custom-lists/${entityId}`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case "widgets":
        window.open(
          `/app/organization/${organizationId}/widgets/${entityId}`,
          "_blank",
          "noopener,noreferrer",
        );
        break;
    }
  };

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      bgColor="bg.lightest"
      border="1px solid"
      borderColor="ink.300"
      borderRadius="150"
      px="200"
      py="150"
      w="full"
    >
      <Flex flexDir="column" gap="0">
        <Text visual="smallSemiBold" lineClamp={1} wordBreak="break-all">
          {getMultilingualString(entityName)}
        </Text>
        {parentName && (
          <Text
            visual="smallRegular"
            color="text.mid"
            lineClamp={1}
            wordBreak="break-all"
          >
            {getMultilingualString(parentName)}
          </Text>
        )}
      </Flex>
      {parentId && (
        <Button
          visual="unstyled"
          Icon={ArrowRightCircle}
          color="icon.mid"
          type="button"
          onClick={handleNavigateToEntity}
        />
      )}
    </Flex>
  );
};

/**
 * Displays the entity usage data for a tag
 * - Groups the entities by type
 * - Within each group, entities are ordered by parent name and then entity name
 * @param param0
 * @returns
 */
export const TagEntityUsage = ({ selectedTagId }: UseTagEntityUsageProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS",
  });
  const loggedInAs = useLoggedInAs();
  const enableDirectorySlideout = useFeatureFlag(FeatureFlags.DirectorySlideout);
  const { entityUsageData, error, loading, selectedEvent, setSelectedEvent } =
    useTagEntityUsage({
      selectedTagId,
    });

  const isAdminland = loggedInAs?.type === EnumOwnerType.TnbInternal;
  const isOrgWithFlyout =
    loggedInAs?.type === EnumOwnerType.Organization && enableDirectorySlideout;
  const useFlyout = isAdminland || isOrgWithFlyout;

  const [flyoutOperatorId, setFlyoutOperatorId] = useState<string | null>(null);
  const [flyoutOrgId, setFlyoutOrgId] = useState<string | undefined>(undefined);

  const operators = entityUsageData?.operators.filter(op => !!op);
  const operatorIds = useMemo(
    () => operators?.map(op => op.entityId) ?? [],
    [operators],
  );
  const orgIdByOperatorId = useMemo(() => {
    const map = new Map<string, string>();
    if (operators) {
      for (const op of operators) {
        map.set(op.entityId, isOrgWithFlyout ? loggedInAs.entityId : op.parentId);
      }
    }
    return map;
  }, [operators, isOrgWithFlyout, loggedInAs]);

  return (
    <>
      <ViewSubSection
        title={t("TAG_DETAILS.INSTANCES.TITLE")}
        description={t("TAG_DETAILS.INSTANCES.DESCRIPTION")}
      >
        {loading ? (
          <Center py="1000">
            <Spinner w={"40px"} h={"40px"} />
          </Center>
        ) : error ? (
          <SimpleErrorNotification />
        ) : (
          <Flex flexDir="column" gap="250">
            {(entityUsageData?.operators.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text visual="smallSemiBold">{t("COMMON_FORM.OPERATORS")}</Text>
                {entityUsageData?.operators
                  .filter(operator => !!operator)
                  .map(operator => (
                    <TagEntity
                      key={operator.entityId}
                      entityType="operators"
                      entityId={operator.entityId}
                      entityName={operator.entityName}
                      parentId={operator.parentId}
                      parentName={operator.parentName}
                      onOpenOperator={
                        useFlyout
                          ? () => {
                              setFlyoutOperatorId(operator.entityId);
                              setFlyoutOrgId(
                                isOrgWithFlyout
                                  ? loggedInAs.entityId
                                  : operator.parentId,
                              );
                            }
                          : undefined
                      }
                    />
                  ))}
              </Flex>
            )}
            {(entityUsageData?.events.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text visual="smallSemiBold">{t("COMMON_FORM.EVENTS")}</Text>
                {entityUsageData?.events
                  .filter(event => !!event)
                  .map(event => (
                    <TagEntity
                      key={event.entityId}
                      entityType="events"
                      entityId={event.entityId}
                      entityName={event.entityName}
                      parentId={event.parentId}
                      parentName={event.parentName}
                      onOpenEvent={() => setSelectedEvent(event)}
                    />
                  ))}
              </Flex>
            )}
            {(entityUsageData?.products.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text
                  visual="smallSemiBold"
                  mt={
                    (entityUsageData?.operators.length ?? 0) > 0 ? "100" : "0"
                  }
                >
                  {t("COMMON_FORM.PRODUCTS")}
                </Text>
                {entityUsageData?.products
                  .filter(product => !!product)
                  .map(product => (
                    <TagEntity
                      key={product.entityId}
                      entityType="products"
                      entityId={product.entityId}
                      entityName={product.entityName}
                      parentId={product.parentId}
                      parentName={product.parentName}
                    />
                  ))}
              </Flex>
            )}
            {(entityUsageData?.lists.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text visual="smallSemiBold">{t("COMMON_FORM.LISTS")}</Text>
                {entityUsageData?.lists
                  .filter(list => !!list)
                  .map(list => (
                    <TagEntity
                      key={list.entityId}
                      entityType="lists"
                      entityId={list.entityId}
                      entityName={list.entityName}
                      parentId={list.parentId}
                      parentName={list.parentName}
                    />
                  ))}
              </Flex>
            )}
            {(entityUsageData?.widgets.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text visual="smallSemiBold">{t("COMMON_FORM.WIDGETS")}</Text>
                {entityUsageData?.widgets
                  .filter(widget => !!widget)
                  .map(widget => (
                    <TagEntity
                      key={widget.entityId}
                      entityType="widgets"
                      entityId={widget.entityId}
                      entityName={widget.entityName}
                      parentId={widget.parentId}
                      parentName={widget.parentName}
                    />
                  ))}
              </Flex>
            )}
            {(entityUsageData?.people.length ?? 0) > 0 && (
              <Flex flexDir="column" gap="100">
                <Text visual="smallSemiBold">{t("COMMON_FORM.PEOPLE")}</Text>
                {entityUsageData?.people
                  .filter(person => !!person)
                  .map(person => (
                    <TagEntity
                      key={person.entityId}
                      entityType="people"
                      entityId={person.entityId}
                      entityName={person.entityName}
                      parentId={person.parentId}
                      parentName={person.parentName}
                    />
                  ))}
              </Flex>
            )}
          </Flex>
        )}
      </ViewSubSection>
      <EventCreateEditModal
        isOpen={!!selectedEvent}
        userEmailAddress={""}
        userFirstName={""}
        userLastName={""}
        userType={"PUBLIC"}
        initialMode={"view"}
        defaultOrganizationId={selectedEvent?.parentId ?? ""}
        defaultOperatorEntityIds={[]}
        defaultOrganizationIdsForOperatorUser={[selectedEvent?.parentId ?? ""]}
        eventId={selectedEvent?.entityId}
        onClose={() => setSelectedEvent(null)}
        onEventUpdated={() => {}}
        onEventDeleted={() => {}}
        hideMenuButtons={true}
      />
      {useFlyout && (
        <OperatorProfileFlyout
          isOpen={!!flyoutOperatorId}
          onClose={() => setFlyoutOperatorId(null)}
          operatorId={flyoutOperatorId}
          organizationId={flyoutOrgId}
          operatorIdsForNavigation={operatorIds}
          onOperatorIdChange={id => {
            setFlyoutOperatorId(id);
            setFlyoutOrgId(orgIdByOperatorId.get(id));
          }}
          placement="rightPane"
        />
      )}
    </>
  );
};
